import { SOURCE_SEPERATOR } from '@/types/commonConst';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import type { SOURCE_TYPE } from '@/types/feedbackConst';
import {
  FEEDBACK_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { getTimezoneDayjs } from '@/utils/time';
import { isEmptyString } from '@/utils/util';

import {
  STRUCTURED_FILTER_OPERATOR,
} from '../model/constants';
import { parseRangeFilter } from './range-filter';

const normalizeTextFilterValueForApi = (
  columnKey: string,
  value: StringNumberValue,
): StringNumberValue => {
  if (typeof value !== 'string') return value;
  if (
    columnKey === FEEDBACK_COLUMNS.HASHTAG ||
    columnKey === FEEDBACK_COLUMNS.MENTION ||
    columnKey === FEEDBACK_COLUMNS.KEYWORD
  ) {
    return value.replace(/^[@#]+/, '').trim();
  }
  return value;
};

const parseSourceKey = (sourceKey: string | number) => {
  return sourceKey.toString().split(SOURCE_SEPERATOR).filter(Boolean);
};

const parseSourceFormats = (
  filterQueryFormats: StructuredFilterQueryFormatT[],
): StructuredFilter | undefined => {
  const sourceValueSets = filterQueryFormats.reduce<
    StructuredFilterSourceObjectSet[]
  >((acc, filterQueryFormat) => {
    const values: SourceObject[] = filterQueryFormat.filterValues.map(
      (filterValue) => {
        const [source, domain, channel] = parseSourceKey(
          filterValue.filterKey,
        );
        return {
          type: Number(source) as SOURCE_TYPE,
          domain: (domain ?? '') as StringNumberValue,
          channel: (channel ?? '') as StringNumberValue,
        };
      },
    );

    acc.push({
      op: filterQueryFormat.operator,
      values,
    });
    return acc;
  }, []);

  if (sourceValueSets.length === 0) {
    return undefined;
  }

  return {
    [SOURCE_COLUMNS.SOURCE_TYPE]: sourceValueSets,
  };
};

export const validateEmptyFilter = (
  filter?: StructuredFilter,
): StructuredFilter | undefined => {
  if (!filter) return undefined;
  if (Object.keys(filter).length === 0) return undefined;
  return filter;
};

export const buildStructuredFilter = (
  filterQueryFormats: StructuredFilterQueryFormatT[],
): StructuredFilter | undefined => {
  let structuredFilter: StructuredFilter = {};

  const sourceFilterQueryFormats = filterQueryFormats.filter(
    (filterQueryFormat) =>
      filterQueryFormat.columnKey === SOURCE_COLUMNS.SOURCE_TYPE,
  );
  const sourceStructures = parseSourceFormats(sourceFilterQueryFormats);
  const rangeFilterQueryFormats = filterQueryFormats.filter(
    (filterQueryFormat) => filterQueryFormat.rangeValues,
  );
  const rangeStructures = parseRangeFilter(rangeFilterQueryFormats);

  structuredFilter = { ...sourceStructures, ...rangeStructures };

  const restFilterQueryFormats = filterQueryFormats.filter(
    (filterQueryFormat) =>
      filterQueryFormat.columnKey !== SOURCE_COLUMNS.SOURCE_TYPE &&
      !filterQueryFormat.rangeValues,
  );

  restFilterQueryFormats.forEach((filterQueryFormat) => {
    const columnKey = filterQueryFormat.columnKey;
    const filterValueSet: StructuredFilterStringNumberValueSet = {
      op: filterQueryFormat.operator,
      values: filterQueryFormat.filterValues.map((filterValue) =>
        normalizeTextFilterValueForApi(columnKey, filterValue.filterKey),
      ),
    };

    if (structuredFilter[columnKey]) {
      if (Array.isArray(structuredFilter[columnKey])) {
        structuredFilter[columnKey].push(filterValueSet);
      } else {
        throw new Error('Invalid structured filter');
      }
    } else {
      structuredFilter[columnKey] = [filterValueSet];
    }
  });

  const hasQueryFilter = restFilterQueryFormats.some(
    (filterQueryFormat) =>
      filterQueryFormat.columnKey === FEEDBACK_COLUMNS.QUERY,
  );

  if (hasQueryFilter) {
    structuredFilter.block_analyzed = true;
  }

  return validateEmptyFilter(structuredFilter);
};

export const convertFilterOperatorToStructuredFilterOperator = (
  operator: FILTER_OPERATOR,
) => {
  switch (operator) {
    case FILTER_OPERATOR.AND:
      return STRUCTURED_FILTER_OPERATOR.AND;
    case FILTER_OPERATOR.OR:
      return STRUCTURED_FILTER_OPERATOR.OR;
    default:
      throw new Error('Invalid filter operator');
  }
};

export const convertStructuredFilterOperatorToFilterOperator = (
  operator: STRUCTURED_FILTER_OPERATOR,
) => {
  switch (operator) {
    case STRUCTURED_FILTER_OPERATOR.AND:
      return FILTER_OPERATOR.AND;
    case STRUCTURED_FILTER_OPERATOR.OR:
      return FILTER_OPERATOR.OR;
    default:
      throw new Error('Invalid structured filter operator');
  }
};

export const convertStructuredFilterToFilterSetQuery = (
  structuredFilter: StructuredFilter,
): FilterSetQuery => {
  const filterSetQuery: FilterSetQuery = {
    filterQueryFormats: [],
  };

  Object.keys(structuredFilter).forEach((columnKey) => {
    const value = structuredFilter[columnKey];
    if (Array.isArray(value)) {
      value.forEach((filterValueSet) => {
        filterSetQuery.filterQueryFormats.push({
          columnKey,
          operator: convertStructuredFilterOperatorToFilterOperator(
            filterValueSet.op,
          ),
          filterValues: filterValueSet.values.map((filterValue) => ({
            filterKey: filterValue as StringNumberValue,
            filterName: filterValue.toString(),
          })),
        });
      });
    } else if (typeof value === 'string') {
      filterSetQuery.filterQueryFormats.push({
        columnKey,
        operator: FILTER_OPERATOR.OR,
        filterValues: [{ filterKey: value, filterName: value }],
      });
    }
  });

  return filterSetQuery;
};

export const buildSingleValue = (
  value?: string | number,
): StructuredFilterStringNumberValueSet[] | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string' && isEmptyString(value)) {
    return undefined;
  }

  return [
    {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [value],
    },
  ];
};

export const buildDateFilter = (dateRange: DateRangeT | null) => {
  if (!dateRange) return undefined;
  return {
    [FEEDBACK_COLUMNS.DATE_START]:
      dateRange?.start_date || getTimezoneDayjs().toJSON(),
    [FEEDBACK_COLUMNS.DATE_END]:
      dateRange?.end_date || getTimezoneDayjs().toJSON(),
  };
};

export const parseDateFilter = (
  filter: StructuredFilter,
): NullableDateRangeT => {
  const startDate = filter[FEEDBACK_COLUMNS.DATE_START];
  if (typeof startDate !== 'string') {
    throw new Error('Invalid date filter');
  }
  const endDate = filter[FEEDBACK_COLUMNS.DATE_END];
  if (typeof endDate !== 'string') {
    throw new Error('Invalid date filter');
  }
  return {
    start_date: startDate || null,
    end_date: endDate || null,
  };
};
